import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import "@/App.css";
import { ArrowRight, BookOpen, Hammer } from "lucide-react";
import { InstrumentCard } from "@/sections";

export default function Home() {
  const [content, setContent] = useState(null);
  const [instruments, setInstruments] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/site-content").then((r) => setContent(r.data)).catch(() => {}),
      api.get("/instruments").then((r) => setInstruments(r.data)).catch(() => {}),
    ]);
  }, []);

  const c = content || {};
  const featured = instruments.filter((i) => i.featured).slice(0, 3);

  const categoryPreview = (type, customImage) => {
    const items = instruments.filter((i) => i.type === type);
    const showcase = items.find((i) => i.image) || items[0];
    const image = (customImage && customImage.trim()) || showcase?.image || "";
    return { items, image, count: items.length };
  };
  const flutes = categoryPreview("flute", c.home_flute_image);
  const drums = categoryPreview("drum", c.home_drum_image);
  const didgeridoos = categoryPreview("didgeridoo", c.home_didgeridoo_image);

  const categories = [
    { type: "flute", path: "/flutes", label: "Pharaoh Love Flutes", desc: "Hand-carved love flutes in NZ Black Maire, Rata and Pohutukawa, tuned to the sacred Egyptian pentatonic.", ...flutes },
    { type: "drum", path: "/drums", label: "Drums", desc: "Hand-crafted drums shaped slowly, in conversation with the wood.", ...drums },
    { type: "didgeridoo", path: "/didgeridoos", label: "Didgeridoos", desc: "Hand-finished didgeridoos with beeswax mouthpieces, tuned for warm grounded drone.", ...didgeridoos },
  ];

  return (
    <div className="App" id="top">
      <Nav artisanName={c.artisan_name || "Sacred Instruments"} />

      {/* HERO */}
      <section className="hero-wood-bg min-h-[100vh] flex items-center px-6 md:px-12 lg:px-24 pt-24">
        <div className="max-w-5xl">
          <p className="label-eyebrow mb-6 fade-up" style={{ color: "#F5F2EB", letterSpacing: "0.3em" }} data-testid="hero-eyebrow">
            {c.hero_tagline || "Honoring Your Magnificence!"}
          </p>
          <h1 className="font-display text-5xl sm:text-7xl lg:text-8xl font-light tracking-tighter leading-[0.95] fade-up"
            style={{ animationDelay: "0.1s", color: "#FFFCF4", textShadow: "0 2px 30px rgba(0,0,0,0.4)" }}
            data-testid="hero-title">
            {c.hero_title || "Sacred Instruments"}
          </h1>
          <p className="mt-8 max-w-xl text-lg md:text-2xl leading-relaxed fade-up"
            style={{ animationDelay: "0.25s", color: "#FAF6EC", textShadow: "0 1px 18px rgba(0,0,0,0.45)" }}
            data-testid="hero-subtitle">
            {c.hero_subtitle || "Hand-crafted Pharaoh Love Flutes, didgeridoos and drums."}
          </p>
          <div className="mt-12 flex flex-wrap gap-4 fade-up" style={{ animationDelay: "0.4s" }}>
            <Link to="/flutes" className="btn-primary" data-testid="hero-cta-explore">
              Explore the collection <ArrowRight size={16} />
            </Link>
            <Link to="/story" className="px-6 py-3 rounded-full border-2 border-white/70 text-white font-medium hover:bg-white/15 hover:border-white transition-all inline-flex items-center gap-2" data-testid="hero-cta-story">
              Read the story <BookOpen size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* CATEGORY PREVIEW CARDS */}
      <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24" id="collections">
        <div className="max-w-7xl mx-auto">
          <p className="label-eyebrow mb-4">The Collection</p>
          <h2 className="font-display text-4xl md:text-6xl font-light tracking-tight leading-[1.05] mb-16 max-w-3xl">
            Three voices. One pair of hands.
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {categories.map((cat) => (
              <Link to={cat.path} key={cat.type} className="instrument-card block group" data-testid={`category-${cat.type}`}>
                <div className="relative overflow-hidden">
                  {cat.image ? (
                    <img src={cat.image} alt={cat.label} className="instrument-image" />
                  ) : (
                    <div className="instrument-image bg-[var(--si-bg)] flex items-center justify-center text-[var(--si-text-2)]">
                      No image yet
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent flex items-end p-6">
                    <p className="text-white text-xs tracking-[0.3em] uppercase font-semibold">
                      {cat.count} piece{cat.count === 1 ? "" : "s"}
                    </p>
                  </div>
                </div>
                <div className="p-6 md:p-8">
                  <h3 className="font-display text-3xl mb-3">{cat.label}</h3>
                  <p className="text-[var(--si-text-2)] leading-relaxed text-sm md:text-base mb-5">{cat.desc}</p>
                  <span className="inline-flex items-center gap-2 text-sm font-medium text-[var(--si-terracotta)]">
                    Explore {cat.label.toLowerCase()} →
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED */}
      {featured.length > 0 && (
        <section className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[var(--si-bg-2)]/40">
          <div className="max-w-7xl mx-auto">
            <p className="label-eyebrow mb-4">Featured pieces</p>
            <h2 className="font-display text-4xl md:text-6xl font-light tracking-tight leading-[1.05] mb-16 max-w-3xl">
              A few standout instruments from the studio right now.
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
              {featured.map((it, i) => <InstrumentCard key={it.id} inst={it} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* CTA STRIP */}
      <section className="py-20 px-6 md:px-12 lg:px-24">
        <div className="max-w-5xl mx-auto admin-card p-10 md:p-14 text-center rounded-3xl bg-[var(--si-bg-dark)]" style={{ color: "var(--si-text-light)" }}>
          <Hammer className="mx-auto mb-4" size={32} style={{ color: "var(--si-terracotta)" }} />
          <h2 className="font-display text-3xl md:text-5xl font-light leading-tight mb-4">
            Commission a piece made just for you.
          </h2>
          <p className="text-[#EFE7D6]/80 text-lg max-w-2xl mx-auto mb-8">
            $200 deposit reserves your spot · 4–6 weeks crafting time. Choose wood, choose style, choose voice.
          </p>
          <Link to="/commissions" className="btn-primary" data-testid="home-cta-commissions">
            Start a commission <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      <Footer content={c} />
    </div>
  );
}
